import * as React from "react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"

/**
 * Calendar size tiers:
 *  'default' → p-2  cell=2rem     text-xs  (standard date-range toolbar)
 *  'compact' → p-1.5 cell=1.75rem  0.7rem  (tighter inline / toolbar use)
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  compact = false,
  size: sizeProp,
  ...props
}) {
  // Resolve the effective size; compact={true} is an alias for size='compact'
  const size = sizeProp ?? (compact ? 'compact' : 'default');
  const isCompact = size === 'compact';

  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        isCompact ? "p-1.5 [--cell-size:1.75rem]" : "p-2 [--cell-size:2rem]",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-1", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[var(--cell-size)] w-[var(--cell-size)] select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[var(--cell-size)] w-[var(--cell-size)] select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[var(--cell-size)] w-full items-center justify-center px-[var(--cell-size)]",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[var(--cell-size)] w-full items-center justify-center gap-1.5 font-medium text-xs",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn("relative", defaultClassNames.dropdown_root),
        dropdown: cn("", defaultClassNames.dropdown),
        caption_label: cn(
          "select-none font-medium text-xs",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weeks: cn("flex flex-col gap-1", defaultClassNames.weeks),
        weekdays: cn("flex gap-1 mb-0", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground flex-1 shrink-0 select-none font-bold text-center uppercase tracking-wide",
          "min-w-[var(--cell-size)] h-[var(--cell-size)] flex items-center justify-center",
          isCompact ? "text-[0.6rem]" : "text-[0.7rem]",
          defaultClassNames.weekday
        ),
        week: cn("flex w-full gap-1", defaultClassNames.week),
        week_number_header: cn("w-[var(--cell-size)] select-none", defaultClassNames.week_number_header),
        week_number: cn(
          "text-muted-foreground select-none",
          isCompact ? "text-[0.6rem]" : "text-[0.7rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative select-none p-0 text-center",
          "w-[var(--cell-size)] h-[var(--cell-size)] shrink-0",
          "[&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md",
          defaultClassNames.day
        ),
        range_start: cn("bg-accent/20 !rounded-l-md", defaultClassNames.range_start),
        range_middle: cn("bg-accent/20 rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent/20 !rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (<div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />);
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (<ChevronLeftIcon className={cn("size-3.5", className)} {...props} />);
          }
          if (orientation === "right") {
            return (<ChevronRightIcon className={cn("size-3.5", className)} {...props} />);
          }
          return null;
        },
        DayButton: (props) => <CalendarDayButton {...props} calSize={size} />,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[var(--cell-size)] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props} />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  calSize = 'default',
  ...props
}) {
  const defaultClassNames = getDefaultClassNames()
  const isCompact = calSize === 'compact';

  const ref = React.useRef(null)
  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-today-unselected={
        modifiers.today &&
        !modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
          ? true
          : undefined
      }
      className={cn(
        "flex w-[var(--cell-size)] h-[var(--cell-size)] items-center justify-center",
        "data-[selected-single=true]:bg-accent data-[selected-single=true]:text-accent-foreground",
        "data-[range-start=true]:bg-accent data-[range-start=true]:text-accent-foreground",
        "data-[range-end=true]:bg-accent data-[range-end=true]:text-accent-foreground",
        "data-[range-middle=true]:bg-accent/20 data-[range-middle=true]:text-primary",
        "data-[range-start=true]:rounded-l-md data-[range-start=true]:rounded-r-none",
        "data-[range-end=true]:rounded-r-md data-[range-end=true]:rounded-l-none",
        "data-[range-middle=true]:rounded-none",
        "data-[today-unselected=true]:bg-accent/15 data-[today-unselected=true]:border data-[today-unselected=true]:border-accent data-[today-unselected=true]:rounded-md data-[today-unselected=true]:text-primary",
        "group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50",
        "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px]",
        "font-normal leading-none",
        isCompact ? "text-[0.65rem] [&>span]:text-[0.6rem]" : "text-xs [&>span]:text-[0.65rem]",
        "[&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props} />
  );
}

export { Calendar, CalendarDayButton }
